import React, { useState, useMemo } from "react";
import { makeStyles } from "@material-ui/core/styles";
import api from "../../../axios";
import Api from "../../../Api";
import {
  Button,
  Box,
  Typography,
  TextField,
  Grid,
  Card,
  CardContent,
  // CardHeader, // Pwede ring gamitin ito
} from "@material-ui/core";
import readXlsxFile from "read-excel-file";
import { useParams } from "react-router-dom";

import Backdrop from "@material-ui/core/Backdrop";
import CircularProgress from "@material-ui/core/CircularProgress";

import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import Alert from '@material-ui/lab/Alert';
import LinearProgress from "@material-ui/core/LinearProgress";
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';

// --- STYLES ---
const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  card: {
    marginBottom: theme.spacing(3),
  },
  inputField: {
    // Para mas maganda ang alignment ng file input
    height: "56px", 
    display: "flex", 
    alignItems: "center",
  },
  table: {
    minWidth: 650,
  },
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: "#fff",
  },
  progressBox: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  successText: {
    color: theme.palette.success.main,
    fontWeight: 'bold',
    marginLeft: theme.spacing(1),
  },
  errorText: {
    color: theme.palette.error.main,
    fontWeight: 'bold',
    marginLeft: theme.spacing(1),
  },
}));

// --- COMPONENT ---
export default function View() {
  const classes = useStyles();
  const { auctionId, setId } = useParams();

  const [message, setMessage] = useState("");
  const [details, setDetails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [successCount, setSuccessCount] = useState(0);
  const [errorCount, setErrorCount] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0); // Para sa progress tracking

  // PROGRESS CALCULATION
  const progress = useMemo(() => {
    if (details.length === 0 || !isUploading) return 0;
    return Math.min(100, (currentIndex / details.length) * 100);
  }, [currentIndex, details.length, isUploading]);
  
  // FILE INPUT HANDLER
  const handleChange = (event) => {
    setMessage(""); // reset message on new upload
    const file = event.target.files[0];
    if (!file) return;

    setLoading(true);
    setDetails([]); // Clear previous data
    setSuccessCount(0); // Reset counts
    setErrorCount(0);
    setCurrentIndex(0);

    readXlsxFile(file).then((rows) => {
      if (rows.length > 0 && rows[0][0] === "Lot number") {
        setDetails(rows.slice(1).map(row => [...row, "", ""])); // Initialize with empty status/message
        setMessage(`✅ ${rows.length - 1} rows loaded successfully. Ready to upload.`);
        setLoading(false);
      } else {
        setLoading(false);
        setMessage("❌ WRONG FORMAT HEADER! Please check the first column header.");
      }
    }).catch(error => {
      setLoading(false);
      setMessage(`❌ An error occurred reading the file: ${error.message}`);
    });
  };

  // UPLOAD HANDLER
  const submitHandler = async (e) => {
    e.preventDefault();
    if (details.length === 0 || isUploading) return;

    setIsUploading(true);
    setSuccessCount(0); 
    setErrorCount(0);
    setCurrentIndex(0);
    setMessage("🚀 Starting upload...");

    // Function to handle a single API call
    const processLot = async (data, index) => {
        const DataArray = {
            auction_id: auctionId,
            set_id: setId,
            lot_number: data[0],
            artist: data[1],
            title: data[2],
            starting_price: data[3],
            signed_dated: data[4],
            medium: data[5],
            size: data[6],
            property: data[7],
            coa: data[8],
            provenance: data[9],
            exhibited: data[10],
            literature: data[11],
            write_up: data[12],
            lot_group: data[13],
            lot_details_html: data[15],
            alias: data[14],
        };

        let config = {
            method: "post",
            url: `${Api.request.URL}/lot`,
            data: DataArray,
        };

        try {
            const response = await api.request(config);
            // Update details array immutably for React
            setDetails(prevDetails => {
                const newDetails = [...prevDetails];
                // Indices 16 and 17 for message and status (success/error)
                newDetails[index] = [...newDetails[index].slice(0, 16), response.data.msg, "success"];
                return newDetails;
            });
            setSuccessCount(prev => prev + 1);
        } catch (error) {
            // Update details array immutably for React
            setDetails(prevDetails => {
                const newDetails = [...prevDetails];
                const errMsg = error.response?.data?.msg || "Network Error/Unknown";
                // Indices 16 and 17 for message and status (success/error)
                newDetails[index] = [...newDetails[index].slice(0, 16), errMsg, "error"];
                return newDetails;
            });
            setErrorCount(prev => prev + 1);
        } finally {
            // Update progress after each item, regardless of success or error
            setCurrentIndex(prev => prev + 1);
        }
    };

    // Use a sequence of promises instead of Promise.all to track progress more accurately
    for (let i = 0; i < details.length; i++) {
        await processLot(details[i], i);
    }
    
    // Final state updates
    setIsUploading(false);
    setMessage(`✅ Upload Complete! Total: ${details.length}, Success: ${successCount}, Error: ${errorCount}.`);
  };

  // HELPER FOR DATE FORMATTING
  const formatDate = (dateValue) => {
    const date = new Date(dateValue);
    if (isNaN(date)) return dateValue;
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className={classes.root}>
      <Card className={classes.card}>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            Excel File Uploader
          </Typography>
          <Grid container spacing={3} alignItems="center">
            {/* FILE INPUT */}
            <Grid item md={6} xs={12} className={classes.inputField}>
              <TextField
                type="file"
                accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                name="file"
                variant="outlined"
                onChange={handleChange}
                fullWidth
              />
            </Grid>
            {/* SEND SERVER BUTTON */}
            <Grid item md={3} xs={12}>
              <Button
                fullWidth
                onClick={submitHandler}
                color="primary"
                variant="contained" // Use contained for better visibility
                style={{ height: "56px" }}
                disabled={details.length === 0 || isUploading || loading}
              >
                {isUploading ? "Uploading..." : "Send to Server"}
              </Button>
            </Grid>
          </Grid>
          
          {/* STATUS MESSAGE & COUNTS */}
          <Box className={classes.progressBox}>
            {message && (
              <Alert 
                severity={message.includes('success') || message.includes('ready') ? "info" : message.includes('complete') ? "success" : "error"} 
                style={{ marginBottom: '8px' }}
              >
                {message}
              </Alert>
            )}

            {details.length > 0 && (
                <Grid container spacing={2}>
                    <Grid item>
                        <Typography variant="subtitle1" className={classes.successText}>
                            <CheckCircleOutlineIcon fontSize="small" style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                            Success: {successCount}
                        </Typography>
                    </Grid>
                    <Grid item>
                        <Typography variant="subtitle1" className={classes.errorText}>
                            <ErrorOutlineIcon fontSize="small" style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                            Error: {errorCount}
                        </Typography>
                    </Grid>
                </Grid>
            )}

            {/* PROGRESS BAR */}
            {isUploading && (
              <Box mt={2}>
                <LinearProgress variant="determinate" value={progress} />
                <Typography variant="body2" color="textSecondary" align="center" style={{ marginTop: '4px' }}>
                    {Math.round(progress)}% Complete ({currentIndex}/{details.length})
                </Typography>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>
      
      {/* DATA TABLE */}
      {details.length > 0 && (
        <TableContainer component={Paper}>
          <Table className={classes.table} aria-label="simple table">
            <TableHead>
              <TableRow>
                <TableCell>Lot No</TableCell>
                <TableCell>Artist</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Starting Price</TableCell>
                <TableCell>Descriptions</TableCell>
                <TableCell align="center">Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {details.map((row, index) => {
                const statusMessage = row[16]; // Message is at index 16
                const status = row[17]; // Status is at index 17 (success/error)
                return (
                  <TableRow key={row[0] + index}>
                    <TableCell component="th" scope="row">{row[0]}</TableCell>
                    <TableCell>{row[1]}</TableCell>
                    <TableCell>{row[2]}</TableCell>
                    <TableCell>{row[3]}</TableCell>
                    <TableCell>
                      {formatDate(row[4])}
                      <br />
                      {row[5]}
                      <br />
                      {row[6]}
                      <br />
                      {row[7]}
                    </TableCell>
                    <TableCell align="center">
                      {status && (
                        <Alert severity={status}>
                          {statusMessage}
                        </Alert>
                      )}
                      {!status && isUploading && currentIndex > index && (
                        <CircularProgress size={20} />
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* GLOBAL LOADING BACKDROP (for file reading only) */}
      <Backdrop className={classes.backdrop} open={loading}>
        <CircularProgress color="inherit" />
        <Typography variant="h6" style={{ marginLeft: 16 }}>Reading file...</Typography>
      </Backdrop>
    </div>
  );
}